import { useState, useEffect } from "react";
import {
  Folder, FolderPlus, FileText, FileImage, FileSpreadsheet, FileArchive, FileVideo,
  File as FileIcon, Upload, Download, Trash2, ChevronRight, ChevronDown, MoreVertical,
  Eye, X, Pencil, History, FolderOpen, Calendar, AlertTriangle, Bell, User,
} from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniDate, formatTanggal } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

// ==================== helpers ====================
export const humanSize = (n) => {
  if (n == null) return "-";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};

export const iconForMime = (ct = "", name = "") => {
  const c = (ct || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (c.startsWith("image/")) return { I: FileImage, tint: "violet" };
  if (c === "application/pdf" || n.endsWith(".pdf")) return { I: FileText, tint: "rose" };
  if (c.includes("spreadsheet") || n.endsWith(".xlsx") || n.endsWith(".csv") || n.endsWith(".xls")) return { I: FileSpreadsheet, tint: "emerald" };
  if (c.includes("zip") || n.endsWith(".zip") || n.endsWith(".rar")) return { I: FileArchive, tint: "amber" };
  if (c.startsWith("video/")) return { I: FileVideo, tint: "sky" };
  if (c.startsWith("text/") || n.endsWith(".txt") || n.endsWith(".md")) return { I: FileText, tint: "slate" };
  return { I: FileIcon, tint: "slate" };
};

export const isPreviewable = (ct = "") => {
  const c = (ct || "").toLowerCase();
  return c === "application/pdf" || c.startsWith("image/") || c.startsWith("text/");
};

export const buildTree = (folders) => {
  const byParent = {};
  folders.forEach(f => {
    const k = f.parent_id || "__root__";
    byParent[k] = byParent[k] || [];
    byParent[k].push(f);
  });
  Object.values(byParent).forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));
  return byParent;
};

export const TAG_COLORS = {
  "Aktif": "emerald",
  "Draft": "sky",
  "Kadaluarsa": "rose",
  "Perlu Review": "amber",
  "Final": "violet",
  "Arsip": "slate",
  "PKWT": "orange",
  "Perpajakan": "indigo",
};
export const EXPIRY_REQUIRED_TAGS = ["PKWT", "Perpajakan"];

export const expiryStatus = (expires_at) => {
  if (!expires_at) return null;
  try {
    const exp = new Date(expires_at + "T00:00:00");
    const today = hariIniDate();
    const days = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return { days, label: `Habis ${Math.abs(days)}h lalu`, color: "rose" };
    if (days === 0) return { days, label: "Habis HARI INI", color: "rose" };
    if (days <= 1) return { days, label: `H-${days}`, color: "rose" };
    if (days <= 7) return { days, label: `H-${days}`, color: "amber" };
    if (days <= 30) return { days, label: `H-${days}`, color: "sky" };
    return { days, label: `${days}h lagi`, color: "slate" };
  } catch { return null; }
};

// ==================== FolderTree (iterative, no self-recursion) ====================
// Flatten the folder tree into a linear list with depth info, then render as flat map.
// This avoids the babel-plugin JSX self-reference recursion that trips on <FolderTree/>
// calling itself inside its own render.
function flattenTree(tree, parentKey, depth, expanded, acc) {
  const children = tree[parentKey] || [];
  for (const f of children) {
    const hasChild = (tree[f.id] || []).length > 0;
    const isOpen = expanded.has(f.id);
    acc.push({ f, depth, hasChild, isOpen });
    if (isOpen && hasChild) {
      flattenTree(tree, f.id, depth + 1, expanded, acc);
    }
  }
  return acc;
}

export function FolderTree({ tree, parentKey, depth, expanded, toggleFolder, currentFolderId, setCurrentFolderId, canWrite, canDelete, onNewSub, onRename, onDelete, onDownloadZip }) {
  const rows = flattenTree(tree, parentKey, depth, expanded, []);
  if (rows.length === 0) return null;
  return (
    <div>
      {rows.map(({ f, depth: d, hasChild, isOpen }) => {
        const isSelected = currentFolderId === f.id;
        return (
          <div key={f.id} className={`group flex items-center gap-1 px-1 py-1 rounded-md hover:bg-emerald-50 mt-0.5 ${isSelected ? "bg-emerald-50 border-l-4 border-emerald-500" : ""}`}
            style={{ paddingLeft: `${d * 12 + 4}px` }}
            data-testid={`folder-${f.id}`}>
            <button onClick={() => toggleFolder(f.id)}
              className={`w-4 h-4 grid place-items-center rounded shrink-0 hover:bg-white ${!hasChild ? "invisible" : ""}`}>
              {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
            <button onClick={() => setCurrentFolderId(f.id)}
              className="flex items-center gap-1.5 flex-1 min-w-0 text-left py-1">
              {isSelected || isOpen ? <FolderOpen className="w-4 h-4 text-emerald-600 shrink-0" /> : <Folder className="w-4 h-4 text-slate-500 shrink-0" />}
              <span className="text-xs font-medium truncate">{f.name}</span>
            </button>
            <span className="text-[9px] px-1 py-0 rounded bg-slate-100 text-slate-600 font-bold shrink-0">
              {f.file_count}
            </span>
            <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
              {canWrite && (
                <button onClick={() => onNewSub(f.id)} title="Buat sub-folder" data-testid={`btn-new-sub-${f.id}`}
                  className="p-1 rounded hover:bg-white text-slate-500 hover:text-emerald-700">
                  <FolderPlus className="w-3 h-3" />
                </button>
              )}
              {canWrite && (
                <button onClick={() => onRename(f)} title="Rename" data-testid={`btn-rename-folder-${f.id}`}
                  className="p-1 rounded hover:bg-white text-slate-500 hover:text-violet-700">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
              <button onClick={() => onDownloadZip(f)} title="Download zip" data-testid={`btn-zip-${f.id}`}
                className="p-1 rounded hover:bg-white text-slate-500 hover:text-amber-700">
                <Download className="w-3 h-3" />
              </button>
              {canDelete && (
                <button onClick={() => onDelete(f)} title="Hapus" data-testid={`btn-del-folder-${f.id}`}
                  className="p-1 rounded hover:bg-white text-slate-400 hover:text-rose-600">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==================== FileCard ====================
export function FileCard({ f, onOpen, onPreview, onDownload, showFolder, folderName }) {
  const { I, tint } = iconForMime(f.content_type, f.original_filename);
  const st = expiryStatus(f.expires_at);
  return (
    <div className="border border-slate-200 rounded-xl bg-white hover:border-emerald-400 hover:shadow-md transition-all overflow-hidden group"
      data-testid={`file-card-${f.id}`}>
      <button onClick={onOpen} className="w-full p-3 text-left flex items-start gap-2">
        <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 shrink-0`}>
          <I className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate" title={f.original_filename}>{f.original_filename}</p>
          <p className="text-[10px] text-slate-500">
            {humanSize(f.size)}
            {f.version > 1 && <span className="ml-1 px-1 py-0 rounded bg-violet-100 text-violet-700 text-[9px] font-bold">v{f.version}</span>}
          </p>
          {showFolder && folderName && (
            <p className="text-[10px] text-slate-500 truncate flex items-center gap-0.5 mt-0.5">
              <Folder className="w-2.5 h-2.5" /> {folderName}
            </p>
          )}
          {st && (
            <p className={`text-[10px] font-semibold text-${st.color}-700 mt-0.5 inline-flex items-center gap-1`}
              data-testid={`file-expiry-${f.id}`}>
              <Calendar className="w-2.5 h-2.5" /> {st.label}
            </p>
          )}
          {f.tags && f.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {f.tags.map(t => (
                <span key={t} className={`text-[9px] px-1.5 py-0 rounded bg-${TAG_COLORS[t] || "slate"}-100 text-${TAG_COLORS[t] || "slate"}-700 font-medium`}>
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </button>
      <div className="border-t border-slate-100 px-2 py-1.5 flex items-center gap-1 bg-slate-50/50">
        <button onClick={onPreview} title="Preview"
          data-testid={`btn-preview-${f.id}`}
          className="p-1.5 rounded hover:bg-white text-slate-500 hover:text-emerald-700 text-xs">
          <Eye className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDownload} title="Download"
          data-testid={`btn-download-${f.id}`}
          className="p-1.5 rounded hover:bg-white text-slate-500 hover:text-emerald-700 text-xs">
          <Download className="w-3.5 h-3.5" />
        </button>
        <button onClick={onOpen} title="Detail" className="ml-auto p-1.5 rounded hover:bg-white text-slate-500 hover:text-slate-700">
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ==================== Simple Modal ====================
// `onSubmit`/`busy`/`disabled`/`dangerous` opsional — TIDAK dipakai pemanggil
// manapun saat ini (lihat useModalGuard.jsx), tapi ditambahkan di sini supaya
// modal baru yang butuh Ctrl+Enter / penjaga isian tinggal mengoper prop,
// tanpa menulis ulang shell-nya. Lihat docstring useModalGuard untuk kapan
// pasang `dangerous`.
export function Modal({ children, onClose, title, testid, width = "max-w-md", onSubmit, busy, disabled, dangerous }) {
  const guard = useModalGuard({ onClose, onSubmit, busy, disabled, dangerous });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={guard.requestClose} data-testid={testid}>
      <div ref={guard.contentRef} className={`bg-white rounded-2xl w-full ${width} shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-semibold text-sm">{title}</h3>
          <button onClick={guard.requestClose} data-testid={testid ? `${testid}-close` : undefined} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid={testid ? `${testid}-guard` : "modal-guard-bar"} />
        )}
      </div>
    </div>
  );
}

// ==================== SumCard ====================
export function SumCard({ I, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <I className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-lg md:text-xl font-bold text-slate-900 truncate">{value}</p>
    </div>
  );
}

// ==================== PreviewModal ====================
export function PreviewModal({ file, url, onClose, onDownload }) {
  const isImg = (file.content_type || "").startsWith("image/");
  const isPdf = (file.content_type || "") === "application/pdf";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col" onClick={onClose} data-testid="preview-modal">
      <div className="p-3 flex items-center justify-between text-white" onClick={e => e.stopPropagation()}>
        <p className="text-sm font-semibold truncate">{file.original_filename}</p>
        <div className="flex gap-2">
          <button onClick={onDownload}
            data-testid="btn-preview-download"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button onClick={onClose} data-testid="btn-preview-close"
            className="p-2 rounded-lg hover:bg-white/20 text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 grid place-items-center p-4 overflow-auto" onClick={e => e.stopPropagation()}>
        {isImg && <img src={url} alt={file.original_filename} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />}
        {isPdf && <iframe src={url} title={file.original_filename} className="w-full h-full min-h-[70vh] bg-white rounded-lg" />}
        {!isImg && !isPdf && (
          <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[70vh] overflow-auto">
            <iframe src={url} title={file.original_filename} className="w-full min-w-[500px] min-h-[400px] border-0" />
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== FileDetailModal ====================
export function FileDetailModal({ file, onClose, allTags, canWrite, canDelete, employees = [], onDownload, onPreview, onToggleTag, onRename, onDelete, onDeleteAllVersions, onShowVersions, onUploadVersion, onUpdateMeta }) {
  const { I, tint } = iconForMime(file.content_type, file.original_filename);
  const needsExpiry = (file.tags || []).some(t => EXPIRY_REQUIRED_TAGS.includes(t));
  const [expInput, setExpInput] = useState(file.expires_at || "");
  const [empInput, setEmpInput] = useState(file.related_employee_id || "");
  const st = expiryStatus(file.expires_at);
  useEffect(() => {
    setExpInput(file.expires_at || "");
    setEmpInput(file.related_employee_id || "");
  }, [file.id, file.expires_at, file.related_employee_id]);
  const empName = employees.find(e => e.id === file.related_employee_id)?.nama;
  return (
    <Modal onClose={onClose} title="Detail File" testid="modal-file-detail" width="max-w-xl">
      <div className="flex items-start gap-3 pb-3 border-b border-slate-100">
        <div className={`w-14 h-14 rounded-xl grid place-items-center bg-${tint}-50 text-${tint}-600 shrink-0`}>
          <I className="w-7 h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 break-words">{file.original_filename}</p>
          <p className="text-[11px] text-slate-500">
            {humanSize(file.size)} · {file.content_type}
            {file.version > 1 && <span className="ml-1 px-1.5 py-0 rounded bg-violet-100 text-violet-700 text-[10px] font-bold">v{file.version}</span>}
          </p>
          <p className="text-[10px] text-slate-500 mt-1">
            Uploaded by <b>{file.uploaded_by_name || file.uploaded_by_email || "-"}</b> · {formatTanggal(file.created_at, { denganJam: true })}
          </p>
          {file.expires_at && st && (
            <p className={`text-[11px] mt-1 font-semibold text-${st.color}-700 inline-flex items-center gap-1`}>
              <Calendar className="w-3 h-3" /> Kadaluarsa: {file.expires_at} · {st.label}
            </p>
          )}
          {empName && (
            <p className="text-[11px] text-slate-600 inline-flex items-center gap-1 mt-0.5">
              <User className="w-3 h-3" /> Terkait: <b>{empName}</b>
            </p>
          )}
        </div>
      </div>

      <div className="py-3 border-b border-slate-100">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Tag / Label</p>
        <div className="flex flex-wrap gap-1.5">
          {(allTags.length ? allTags : ["Aktif","Draft","Kadaluarsa","Perlu Review","Final","Arsip","PKWT","Perpajakan"]).map(t => {
            const active = (file.tags || []).includes(t);
            const isExpTag = EXPIRY_REQUIRED_TAGS.includes(t);
            return (
              <button key={t} onClick={() => canWrite && onToggleTag(t)}
                disabled={!canWrite}
                data-testid={`tag-btn-${t}`}
                className={`text-xs px-3 py-1 rounded-full font-medium border transition-colors ${
                  active
                    ? `bg-${TAG_COLORS[t] || "slate"}-100 border-${TAG_COLORS[t] || "slate"}-400 text-${TAG_COLORS[t] || "slate"}-800`
                    : `bg-white border-slate-200 text-slate-500 hover:border-slate-300 ${!canWrite ? "opacity-60 cursor-not-allowed" : ""}`
                }`}>
                {active ? "✓ " : ""}{t}{isExpTag ? " ⏰" : ""}
              </button>
            );
          })}
        </div>
      </div>

      {(needsExpiry || file.expires_at || file.related_employee_id) && (
        <div className="py-3 border-b border-slate-100 bg-amber-50/40 rounded-lg -mx-2 px-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800 mb-2 inline-flex items-center gap-1">
            <Bell className="w-3 h-3" /> Reminder Kadaluarsa
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Tanggal Kadaluarsa {needsExpiry && <span className="text-rose-600">*</span>}</span>
              <DatePicker value={expInput} onChange={setExpInput}
                disabled={!canWrite}
                data-testid="detail-expiry"
                className="mt-1" />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Karyawan Terkait</span>
              <select value={empInput} onChange={e => setEmpInput(e.target.value)}
                disabled={!canWrite}
                data-testid="detail-related-emp"
                className="mt-1 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white disabled:bg-slate-100">
                <option value="">— tidak dikaitkan —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.nama} · {e.branch || "-"}</option>)}
              </select>
            </label>
          </div>
          {canWrite && (expInput !== (file.expires_at || "") || empInput !== (file.related_employee_id || "")) && (
            <button onClick={() => onUpdateMeta({ expires_at: expInput || null, related_employee_id: empInput || null })}
              data-testid="btn-save-expiry"
              className="mt-2 h-8 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium">
              Simpan
            </button>
          )}
          <p className="text-[10px] text-amber-800 mt-1.5 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Bot Telegram akan ping <b>supervisor karyawan terkait</b> + <b>grup HR</b> pada H-30, H-7, H-1 sebelum kadaluarsa.</span>
          </p>
        </div>
      )}

      <div className="py-3 flex flex-wrap gap-2">
        <button onClick={onPreview}
          data-testid="btn-detail-preview"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-medium">
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
        <button onClick={onDownload}
          data-testid="btn-detail-download"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium">
          <Download className="w-3.5 h-3.5" /> Download
        </button>
        <button onClick={onShowVersions}
          data-testid="btn-detail-versions"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-violet-100 hover:bg-violet-200 text-violet-800 text-xs font-medium">
          <History className="w-3.5 h-3.5" /> Riwayat Versi
        </button>
        {canWrite && (
          <button onClick={onUploadVersion}
            data-testid="btn-detail-new-version"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-800 text-xs font-medium">
            <Upload className="w-3.5 h-3.5" /> Upload Versi Baru
          </button>
        )}
        {canWrite && (
          <button onClick={onRename}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium">
            <Pencil className="w-3.5 h-3.5" /> Rename
          </button>
        )}
        {canDelete && (
          <>
            <button onClick={onDelete}
              data-testid="btn-detail-delete"
              className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-medium">
              <Trash2 className="w-3.5 h-3.5" /> Hapus Versi Ini
            </button>
            {file.version > 1 && (
              <button onClick={onDeleteAllVersions}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium">
                <Trash2 className="w-3.5 h-3.5" /> Hapus Semua Versi
              </button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
